import React, { FunctionComponent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { get, sortBy } from 'lodash';
import { ISelectBoardInputFormProps, ISelectInputFormProps } from '../types';
import Select, { OptionsType, OptionTypeBase, StylesConfig } from 'react-select';
import { useSelect } from '../../../../../utils/selector.utils';
import { boardSelectors } from '../../../../../app/resource/boards/board/board.selectors';
import { IBoard } from '../../../../../app/resource/boards/board/board.types';

const noBoardOption = {
  value: '',
  label: 'No Board',
};

export const SelectBoardInput: FunctionComponent<ISelectBoardInputFormProps> = ({
  id = 'board',
  name = 'board',
  defaultBoardId,
  dependencyFieldName = 'meeting',
  allowEmpty,
  validation,
  disabled,
  placeholder = 'Select Board',
  rightIcon,
  leftIcon,
  label = 'Board',
  isSingle,
  className,
  clearAllActionAvailable = true,
  onInputChange,
  onGetOptionLabel,
}) => {
  const { control, errors, setError, reset, getValues, clearErrors } = useFormContext();

  useEffect(() => {
    if (validation) {
      if (!errors[name] && getValues()[name] && getValues()[name].length < 1) {
        if (name === 'status') {
          setError(name, { type: 'minLength', message: 'You need to select a status to save' });
        } else {
          setError(name, { type: 'minLength', message: 'You need to add at least one entity' });
        }
      }
    } else if (errors[name]) {
      clearErrors(name);
    }
  }, [getValues, errors, name, setError, clearErrors, validation]);

  const handleChange = (e: any, controllerProps: any) => {
    if (onInputChange) {
      onInputChange(controllerProps.name);
    }
    controllerProps.onChange(e);
    setChanged(true);
  };

  const CustomClearIndicator = () => {
    return null;
  };

  const buildStyle = (items: number) => {
    const MIN_ITEMS_FOR_UPDATE_UI = 2;
    const customStyles: StylesConfig<OptionsType<OptionTypeBase>, true> = {
      control: (customStyles) => ({
        ...customStyles,
        boxShadow: '0 !important',
        borderColor: '#edf2f5',
        paddingTop: items >= MIN_ITEMS_FOR_UPDATE_UI ? 10 : undefined, // Fix UI padding error when too many items are selected
        '&:hover': {
          borderColor: '#edf2f5',
          backgroundColor: '#edf2f5',
        },
      }),
      multiValue: (customStyles) => ({
        ...customStyles,
        height: 'auto',
      }),
      valueContainer: (customStyles) => ({
        ...customStyles,
        flexWrap: 'wrap',
        maxHeight: '150px',
        overflowY: items >= MIN_ITEMS_FOR_UPDATE_UI ? 'auto' : undefined, // Fix UI scroll error when too many items are selected
      }),
      menuList: (customStyles) => ({
        ...customStyles,
        maxHeight: '150px',
        overflowY: 'scroll',
      }),
      placeholder: (customStyles) => ({
        ...customStyles,
        color: '#D8D8D8',
        fontWeight: 'bold',
        letterSpacing: '0.4px',
      }),
      indicatorSeparator: (customStyles) => ({
        ...customStyles,
        borderColor: '#edf2f5',
      }),
    };
    return customStyles;
  };

  const [changed, setChanged] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState('');
  const [boardOptions, setBoardOptions] = useState<any | any[]>([noBoardOption]);
  const [defaultValue, setDefaultValue] = useState<{ value: string; label: string } | undefined>(noBoardOption);

  const formProps = useFormContext();
  const focusMeetingTuple = formProps.watch(dependencyFieldName);

  let boards = [] as IBoard[];
  let meetingId = '';
  if (focusMeetingTuple) {
    meetingId = focusMeetingTuple.value;
  }

  boards = useSelect(boardSelectors.selectResourceList).filter((board) =>
    board.meetingIds.includes(meetingId),
  ) as IBoard[];

  useEffect(() => {
    if (focusMeetingTuple && focusMeetingTuple.value !== selectedMeetingId && boards.length > 0) {
      setSelectedMeetingId(focusMeetingTuple.value);
      let boardOptions:
        | {
            value: string;
            label: string;
          }
        | {
            value: string;
            label: string;
          }[] = [];

      if (allowEmpty) {
        boardOptions.unshift(noBoardOption);
      }

      if (boards.length != 0) {
        boardOptions = boardOptions.concat(
          sortBy(
            boards.map((board) => board && { value: board.id, label: board.name }),
            ['label'],
          ),
        );
      }

      setBoardOptions(boardOptions);

      let foundOption: any;

      if (boardOptions.length == 1 && boardOptions[0].value == '') {
        setDefaultValue(boardOptions[0]);
      } else if (!changed) {
        if (defaultBoardId) {
          foundOption = boardOptions.find((boardOption) => boardOption && boardOption.value === defaultBoardId);
          if (foundOption) {
            setDefaultValue(foundOption);
          } else {
            setDefaultValue({ value: '', label: '' });
          }
        } else {
          setDefaultValue(boardOptions[0]);
        }
      }

      setChanged(true);
      if (foundOption) {
        reset({
          [name]: foundOption,
        });
      }
    }
  }, [
    name,
    reset,
    setSelectedMeetingId,
    selectedMeetingId,
    focusMeetingTuple,
    setBoardOptions,
    boards,
    defaultBoardId,
    changed,
    allowEmpty,
  ]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ''}
      rules={validation}
      disabled={disabled}
      render={(controllerProps) => (
        <>
          <div className="flex">
            {!!get(errors, name, false) && (
              <label htmlFor={name} style={{ color: 'red' }}>
                {`${label ? label + ' - ' : ''} ${get(errors, `${name}.message`)}`}
              </label>
            )}
            {!get(errors, name, false) && <label htmlFor={name}>{label}</label>}
            {!allowEmpty && (
              <i
                className="f-1 icon-info yellow ml-1"
                data-tooltip="Card must be an action item to remove it from boards"
              />
            )}
          </div>
          {leftIcon}
          {rightIcon}
          <Select
            defaultValue={defaultValue || ''}
            id={id}
            className={`custom-select-new ${className}`}
            name={controllerProps.name}
            disabled={disabled}
            onChange={(e) => handleChange(e, controllerProps)}
            options={boardOptions}
            value={controllerProps.value}
            placeholder={placeholder}
            isMulti={isSingle ? undefined : true}
            closeMenuOnSelect={isSingle}
            styles={buildStyle(controllerProps.value.length)}
            getOptionLabel={onGetOptionLabel}
            components={clearAllActionAvailable ? undefined : { ClearIndicator: CustomClearIndicator }}
          />
        </>
      )}
    />
  );
};
